import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, Search, MapPin, Info, Loader2, Copy, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface IPInfo {
  ip: string;
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  postal?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  isp?: string;
  org?: string;
  as?: string;
  hostname?: string;
  mobile?: boolean;
  proxy?: boolean;
  vpn?: boolean;
  tor?: boolean;
  threat?: string;
  bogon?: boolean;
}

const IPLookupTool: React.FC = () => {
  const [ipAddress, setIpAddress] = useState('');
  const [ipInfo, setIpInfo] = useState<IPInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userIP, setUserIP] = useState<string>('');

  useSEO({
    title: 'IP Lookup Tool - Get Detailed IP Address Information & Geolocation',
    description: 'Look up IP address information including location, ISP, organization, and security details. Get geolocation data for any IPv4 or IPv6 address.',
    keywords: 'IP lookup, IP address lookup, geolocation, IP location, IP information, IPv4, IPv6, ISP lookup, IP tracker',
    canonical: 'https://snap-tools.online/security/ip-lookup-tool'
  });

  // Get user's current IP
  const getUserIP = async () => {
    setIsLoading(true);
    try {
      // Using a free IP detection service
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      setUserIP(data.ip);
      setIpAddress(data.ip);
      toast.success('Your IP address has been detected');
    } catch (error) {
      console.error('Error getting user IP:', error);
      toast.error('Could not detect your IP address');
    } finally {
      setIsLoading(false);
    }
  };

  // Validate IP address format
  const isValidIP = (ip: string): boolean => {
    // IPv4 regex
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    // IPv6 regex (simplified)
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;
    
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  };

  // Lookup IP information
  const lookupIP = async () => {
    if (!ipAddress.trim()) {
      toast.error('Please enter an IP address');
      return;
    }

    if (!isValidIP(ipAddress.trim())) {
      toast.error('Please enter a valid IP address');
      return;
    }

    setIsLoading(true);
    setIpInfo(null);

    try {
      // Using ip-api.com service (free tier)
      const response = await fetch(`http://ip-api.com/json/${ipAddress.trim()}?fields=status,message,country,countryCode,region,regionName,city,zip,lat,lon,timezone,isp,org,as,mobile,proxy,hosting,query`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch IP information');
      }

      const data = await response.json();
      
      if (data.status === 'fail') {
        throw new Error(data.message || 'Invalid IP address');
      }

      // Transform the response to match our interface
      const transformedData: IPInfo = {
        ip: data.query,
        city: data.city,
        region: data.regionName,
        country: data.country,
        country_code: data.countryCode,
        postal: data.zip,
        latitude: data.lat,
        longitude: data.lon,
        timezone: data.timezone,
        isp: data.isp,
        org: data.org,
        as: data.as,
        mobile: data.mobile,
        proxy: data.proxy,
        vpn: data.hosting, // Using hosting as a proxy for VPN detection
      };

      setIpInfo(transformedData);
      toast.success('IP information retrieved successfully');
    } catch (error) {
      console.error('Error looking up IP:', error);
      toast.error('Failed to lookup IP information. Please try again.');
      
      // Fallback with mock data for demonstration
      const mockData: IPInfo = {
        ip: ipAddress.trim(),
        city: 'Unknown',
        region: 'Unknown',
        country: 'Unknown',
        country_code: 'XX',
        postal: 'Unknown',
        latitude: 0,
        longitude: 0,
        timezone: 'Unknown',
        isp: 'Unknown ISP',
        org: 'Unknown Organization',
        as: 'Unknown AS',
        mobile: false,
        proxy: false,
        vpn: false,
      };
      setIpInfo(mockData);
      toast('Showing example data - API service may be unavailable', {
        icon: 'ℹ️',
        duration: 4000
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Copy IP info to clipboard
  const copyInfo = async (info: string, label: string) => {
    try {
      await navigator.clipboard.writeText(info);
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Open location in maps
  const openInMaps = () => {
    if (ipInfo?.latitude && ipInfo?.longitude) {
      const url = `https://www.google.com/maps?q=${ipInfo.latitude},${ipInfo.longitude}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Globe className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">IP Lookup Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get detailed information about any IP address including geolocation, ISP details, and security information. Works with both IPv4 and IPv6 addresses.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* IP Input Section */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IP Address:
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    placeholder="Enter IP address (e.g., 8.8.8.8)"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && lookupIP()}
                  />
                  <button
                    onClick={getUserIP}
                    disabled={isLoading}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    My IP
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={lookupIP}
                  disabled={isLoading || !ipAddress.trim()}
                  className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {isLoading ? 'Looking up...' : 'Lookup IP'}
                </button>
              </div>
            </div>
          </div>

          {/* Your IP Display */}
          {userIP && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900">Your IP Address:</h3>
                  <div className="font-mono text-blue-700">{userIP}</div>
                </div>
                <button
                  onClick={() => copyInfo(userIP, 'Your IP')}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
            </motion.div>
          )}

          {/* IP Information Results */}
          {ipInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-6 mb-6"
            >
              <div className="flex items-center gap-2 mb-6">
                <Info className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">IP Information for {ipInfo.ip}</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Location Information */}
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Country:</span>
                      <span className="font-medium">{ipInfo.country} ({ipInfo.country_code})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Region:</span>
                      <span className="font-medium">{ipInfo.region || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">City:</span>
                      <span className="font-medium">{ipInfo.city || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Postal Code:</span>
                      <span className="font-medium">{ipInfo.postal || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timezone:</span>
                      <span className="font-medium">{ipInfo.timezone || 'Unknown'}</span>
                    </div>
                    {ipInfo.latitude && ipInfo.longitude && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Coordinates:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-xs">{ipInfo.latitude.toFixed(4)}, {ipInfo.longitude.toFixed(4)}</span>
                          <button
                            onClick={openInMaps}
                            className="text-purple-600 hover:text-purple-700"
                            title="Open in Google Maps"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Network Information */}
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Network
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">ISP:</span>
                      <span className="font-medium">{ipInfo.isp || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Organization:</span>
                      <span className="font-medium">{ipInfo.org || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">AS Number:</span>
                      <span className="font-medium">{ipInfo.as || 'Unknown'}</span>
                    </div>
                    {ipInfo.hostname && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hostname:</span>
                        <span className="font-medium text-xs">{ipInfo.hostname}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Information */}
                <div className="bg-white rounded-lg p-4 md:col-span-2">
                  <h4 className="font-semibold text-gray-900 mb-3">Security & Risk Assessment</h4>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <span className="text-gray-600">Mobile Connection:</span>
                      <span className={`font-medium ${ipInfo.mobile ? 'text-orange-600' : 'text-green-600'}`}>
                        {ipInfo.mobile ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <span className="text-gray-600">Proxy/VPN:</span>
                      <span className={`font-medium ${ipInfo.proxy || ipInfo.vpn ? 'text-red-600' : 'text-green-600'}`}>
                        {ipInfo.proxy || ipInfo.vpn ? 'Detected' : 'None'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <span className="text-gray-600">Tor Network:</span>
                      <span className={`font-medium ${ipInfo.tor ? 'text-red-600' : 'text-green-600'}`}>
                        {ipInfo.tor ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Copy All Info Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    const infoText = `IP: ${ipInfo.ip}\nLocation: ${ipInfo.city}, ${ipInfo.region}, ${ipInfo.country}\nISP: ${ipInfo.isp}\nOrganization: ${ipInfo.org}\nCoordinates: ${ipInfo.latitude}, ${ipInfo.longitude}`;
                    copyInfo(infoText, 'IP Information');
                  }}
                  className="flex items-center gap-2 mx-auto bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy All Information
                </button>
              </div>
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">What is IP Lookup?</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Geolocation:</strong> Determine approximate physical location</li>
                <li>• <strong>ISP Information:</strong> Identify internet service provider</li>
                <li>• <strong>Network Details:</strong> Get organization and AS number</li>
                <li>• <strong>Security Analysis:</strong> Detect proxies, VPNs, and threats</li>
                <li>• <strong>Troubleshooting:</strong> Help diagnose network issues</li>
                <li>• <strong>Compliance:</strong> Meet geo-blocking and regulatory requirements</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Common Use Cases</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Fraud Prevention:</strong> Verify user location and detect suspicious activity</li>
                <li>• <strong>Content Delivery:</strong> Route users to nearest servers</li>
                <li>• <strong>Website Analytics:</strong> Understand visitor demographics</li>
                <li>• <strong>Cybersecurity:</strong> Investigate potential threats and attacks</li>
                <li>• <strong>Network Administration:</strong> Monitor and manage network resources</li>
                <li>• <strong>Compliance:</strong> Enforce geographic restrictions and regulations</li>
              </ul>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="font-semibold text-yellow-900 mb-2">Privacy & Accuracy Notice</h4>
            <div className="text-sm text-yellow-800 space-y-1">
              <p>• IP geolocation is approximate and may not reflect exact physical location</p>
              <p>• VPN and proxy users may show different locations than their actual position</p>
              <p>• This tool uses public IP databases and third-party services</p>
              <p>• No personal information is stored or tracked by this application</p>
              <p>• Results are for informational purposes only</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IPLookupTool;
